import {
  getTextInnerGlow,
  scaleTextInnerGlow,
} from '../utils/getTextInnerGlow';
import { TextInnerGlowFilter } from './textInnerGlowFilter';
import { getTextDepth, scaleTextDepth } from '../utils/getTextDepth';
import { TextDepthFilter } from './textDepthFilter';
import * as React from 'react';
import { memo, useCallback, useMemo, useState } from '@web-stories-wp/react';
import { v4 as uuidv4 } from 'uuid';
import type { CSSProperties, SVGProps } from 'react';
import { hasGradient, type Pattern } from '@web-stories-wp/patterns';
import {
  applyTextTransform,
  generateFontFamily,
  getTextLineGeometry,
  selectFontMetrics,
} from '../utils';
import type { TextLineGeometry } from '../utils/textLineGeometry';
import { getSpansForElement } from './getSpansForElement';
import { getTextContentWidth } from '../utils/textBoxMetrics';
import { type TextElement, type TextElementFont } from '../../types';
import { SvgTextGradient } from './svgTextGradient';
import { getStrokeProps } from './getStrokeProps';
import type { FontConfig } from '../../../app/font/types';
import type { Transform } from '../../../components/transform/types';
import { getNormalizedTextShadow } from '../getTextShadowStyles';
import useTextLayoutElement from '../utils/useTextLayoutElement';
import useGradientPreview from '../useGradientPreview';

import {
  getTextTexturePaint,
  getTextTextureTransform,
} from '../utils/getTextTextureTransform';

type SvgTextDisplayProps = {
  dataToEditorX: (value: number) => number;
  dataToEditorY: (value: number) => number;
  element: TextElement;
  textureOrigin?: { x: number; y: number };
  fillGradientBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  font: TextElementFont;
  fontLoaded?: Omit<FontConfig, 'content'>[];
  fontSize: number;
  height?: number;
  left?: number;
  lineHeight: number;
  paddingBetweenLines?: number;
  top?: number;
  width?: number;
  styles?: CSSProperties;
  type?: 'edit' | 'display';
  initialGradientPreview?: Transform['gradientPreview'];
  cachePreview?: boolean;
};

export const SvgTextDisplay = memo(function SvgTextDisplay({
  dataToEditorX,
  dataToEditorY,
  element: sourceElement,
  fillGradientBounds,
  textureOrigin,
  font,
  fontLoaded,
  fontSize,
  height: heightOverride,
  left = 0,
  lineHeight,
  top = 0,
  paddingBetweenLines = 0,
  width: widthOverride,
  styles,
  type = 'display',
  initialGradientPreview,
  cachePreview = false,
}: SvgTextDisplayProps) {
  const preview =
    useGradientPreview(sourceElement.id) || initialGradientPreview;
  const element = useMemo(
    () =>
      preview?.content
        ? { ...sourceElement, content: preview.content }
        : sourceElement,
    [sourceElement, preview?.content]
  );
  const inlineGradientPreviewBounds = preview?.inlineBounds;
  const fillPattern = preview?.color || element.color;
  const strokeElement = preview?.textBorder
    ? { ...element, textBorder: preview.textBorder }
    : element;
  const [effectInstanceId] = useState(uuidv4);
  const textTexture = element.textTexture;
  const layoutElement = useTextLayoutElement(element);
  const widthWithoutStrokeOffset = useMemo(
    () =>
      getTextContentWidth({
        element,
        dataToEditorX,
        width: widthOverride ?? dataToEditorX(element.width),
      }),
    [dataToEditorX, element, widthOverride]
  );
  const providedHeightWithoutStrokeOffset = useMemo(
    () => heightOverride ?? dataToEditorY(element.height),
    [dataToEditorY, element.height, heightOverride]
  );
  const textureWidth = useMemo(
    () => dataToEditorX(element.width),
    [dataToEditorX, element.width]
  );
  const textureHeight = useMemo(
    () => dataToEditorY(element.height),
    [dataToEditorY, element.height]
  );

  const insideTextClipPathId = `text-fill-inside-${element.id}-${type}`;
  const outsideTextClipPathId = `text-fill-outside-${element.id}-${type}`;
  const strokeGradientId = `text-stroke-gradient-${element.id}-${type}`;
  const fillGradientId = `text-fill-gradient-${element.id}-${type}`;
  const fillTextureId = `text-fill-texture-${element.id}-${type}`;
  const shadowFilterId = `text-shadow-${element.id}-${type}`;

  const hasFillGradient = useMemo(
    () => hasGradient(fillPattern),
    [fillPattern]
  );
  const depth = getTextDepth(element.textDepth);
  const depthFilterId = `text-depth-${element.id}-${type}-${effectInstanceId}`;
  const previewDepthFor = useCallback(
    (angle: number) => {
      if (!element.textDepth) return null;
      const preview = getTextDepth({
        ...element.textDepth,
        lightDirection: angle,
      });
      return (
        preview && scaleTextDepth(preview, dataToEditorX(1), dataToEditorY(1))
      );
    },
    [element.textDepth, dataToEditorX, dataToEditorY]
  );
  const scaledDepth =
    depth && scaleTextDepth(depth, dataToEditorX(1), dataToEditorY(1));
  const glow = getTextInnerGlow(element.textInnerGlow);
  const glowFilterId = `text-inner-glow-${element.id}-${type}-${effectInstanceId}`;
  const scaledGlow = glow && scaleTextInnerGlow(glow, dataToEditorY(1));
  const hasTextTexture = Boolean(element.textTexture?.src);
  const normalizedShadow = useMemo(
    () => getNormalizedTextShadow(element),
    [element]
  );
  const hasShadow = normalizedShadow?.target === 'text';

  const { insideStrokeWidth, outsideStrokeWidth, ...sharedStrokeStyles } =
    getStrokeProps({ dataToEditorY, element: strokeElement, strokeGradientId });

  // Use the CSS line-box baseline for every metric-backed SVG path. Standard
  // trim removes half-leading on the wrapper, leaving the glyph baseline at
  // the font ascent regardless of line height.
  const baselineOffset = useMemo(() => {
    const metrics = selectFontMetrics(element.font);
    if (!metrics) {
      return null;
    }

    const ascent = (fontSize * metrics.asc) / metrics.upm;
    const descent = (fontSize * Math.abs(metrics.des)) / metrics.upm;
    const lineHeightInPixels = lineHeight * fontSize;
    const leading = lineHeightInPixels - ascent - descent;

    return leading / 2 + ascent;
  }, [element.font, fontSize, lineHeight]);

  const dominantBaseline =
    baselineOffset === null ? 'text-before-edge' : 'alphabetic';

  const sharedTextStyles: CSSProperties = {
    fontFamily: generateFontFamily(font),
    fontSize: `${fontSize}px`,
    fontWeight: element.fontWeight || 700,
    fontStyle: element.fontStyle || 'normal',
    dominantBaseline,
    lineHeight,
  };

  const strokeOffset = useMemo(
    () => dataToEditorY(outsideStrokeWidth * 2),
    [outsideStrokeWidth, dataToEditorY]
  );

  const width = useMemo(
    () => widthWithoutStrokeOffset + strokeOffset * 2,
    [widthWithoutStrokeOffset, strokeOffset]
  );

  const lineHeightInPixels = useMemo(
    () => lineHeight * fontSize,
    [fontSize, lineHeight]
  );
  const lineSpacing = useMemo(
    () => lineHeightInPixels + paddingBetweenLines * 2,
    [lineHeightInPixels, paddingBetweenLines]
  );

  const sharedTextProps: SVGProps<SVGTextElement> = {};

  const spans = useMemo(
    () =>
      getSpansForElement({
        dataToEditorX,
        dataToEditorY,
        text: layoutElement,
        width: widthWithoutStrokeOffset,
      }),
    [
      dataToEditorX,
      dataToEditorY,
      layoutElement,
      fontLoaded,
      widthWithoutStrokeOffset,
    ]
  );
  const geometricListMarkerTextOffsetY = useMemo(
    () =>
      getGeometricListMarkerTextOffsetY({
        baselineOffset,
        font: element.font,
        fontSize,
        lineHeightInPixels,
        spans,
      }),
    [baselineOffset, element.font, fontSize, lineHeightInPixels, spans]
  );
  const hasMixedTypography = useMemo(
    () =>
      spans.some(
        (span) =>
          Boolean(span.fontReferenceId) ||
          span.fontSizeOverride !== undefined ||
          span.lineHeightOverride !== undefined
      ),
    [spans]
  );
  const mixedLineGeometry = useMemo(
    () =>
      hasMixedTypography
        ? getTextLineGeometry({
            spans,
            defaultFont: font,
            defaultFontSize: fontSize,
            lineHeight,
            paddingBetweenLines,
          })
        : [],
    [font, fontSize, hasMixedTypography, lineHeight, paddingBetweenLines, spans]
  );
  const mixedLineGeometryByNumber = useMemo<Map<number, TextLineGeometry>>(
    () =>
      new Map(
        mixedLineGeometry.map((line) => {
          const lineOffsetY = Math.max(
            0,
            ...spans
              .filter((span) => span.lineNumber === line.lineNumber)
              .map((span) => span.lineOffsetY)
          );
          return [
            line.lineNumber,
            {
              ...line,
              top: line.top + lineOffsetY,
              baseline: line.baseline + lineOffsetY,
              bottom: line.bottom + lineOffsetY,
            },
          ] as const;
        })
      ),
    [mixedLineGeometry, spans]
  );

  const minimumTextHeightWithoutStrokeOffset = useMemo(() => {
    if (hasMixedTypography) {
      return mixedLineGeometry.reduce(
        (maximum, line) =>
          Math.max(
            maximum,
            mixedLineGeometryByNumber.get(line.lineNumber)?.bottom || 0
          ),
        0
      );
    }
    if (
      element.verticalTrim !== 'baseline' ||
      baselineOffset === null ||
      spans.length === 0
    ) {
      return 0;
    }

    const metrics = element.font.metrics;

    if (!metrics || !metrics.upm || metrics.des === undefined) {
      return 0;
    }

    const lastLineNumber = Math.max(...spans.map((span) => span.lineNumber));
    const descent = (fontSize * Math.abs(metrics.des)) / metrics.upm;

    const lastLineOffsetY = Math.max(
      ...spans
        .filter((span) => span.lineNumber === lastLineNumber)
        .map((span) => span.lineOffsetY)
    );

    return (
      lineSpacing * lastLineNumber + lastLineOffsetY + baselineOffset + descent
    );
  }, [
    baselineOffset,
    element.font.metrics,
    element.verticalTrim,
    fontSize,
    hasMixedTypography,
    lineSpacing,
    mixedLineGeometry,
    mixedLineGeometryByNumber,
    spans,
  ]);

  const heightWithoutStrokeOffset = useMemo(
    () =>
      Math.max(
        providedHeightWithoutStrokeOffset,
        minimumTextHeightWithoutStrokeOffset
      ),
    [minimumTextHeightWithoutStrokeOffset, providedHeightWithoutStrokeOffset]
  );

  const height = useMemo(
    () => heightWithoutStrokeOffset + strokeOffset * 2,
    [heightWithoutStrokeOffset, strokeOffset]
  );

  const shadowProps = useMemo(() => {
    if (!hasShadow) {
      return null;
    }

    const shadowX = dataToEditorX(normalizedShadow.x);
    const shadowY = dataToEditorY(normalizedShadow.y);
    const shadowBlur = dataToEditorY(normalizedShadow.blur);
    const shadowColor = normalizedShadow.color;
    const blurPadding = shadowBlur * 3;
    const spread = Math.max(Math.abs(shadowX), Math.abs(shadowY)) + blurPadding;

    return {
      x: -spread,
      y: -spread,
      width: width + spread * 2,
      height: height + spread * 2,
      dx: shadowX,
      dy: shadowY,
      blur: shadowBlur,
      color: `rgb(${shadowColor.r}, ${shadowColor.g}, ${shadowColor.b})`,
      opacity: shadowColor.a ?? 1,
    };
  }, [
    dataToEditorX,
    dataToEditorY,
    hasShadow,
    height,
    normalizedShadow,
    width,
  ]);

  const lineContentMetrics = useMemo(() => {
    const metrics = new Map<number, { inset: number; end: number }>();

    for (const span of spans) {
      if (span.isListMarker) {
        continue;
      }

      const current = metrics.get(span.lineNumber);
      metrics.set(span.lineNumber, {
        inset: Math.min(current?.inset ?? span.lineInset, span.lineInset),
        end: Math.max(current?.end ?? span.lineInset, span.x + span.width),
      });
    }

    return metrics;
  }, [spans]);

  const lineAlignOffsets = useMemo(() => {
    const offsets = new Map<number, number>();
    const textAlign =
      hasMixedTypography && element.textAlign === 'justify'
        ? 'left'
        : element.textAlign;

    lineContentMetrics.forEach(({ inset, end }, lineNumber) => {
      const contentWidth = Math.max(0, end - inset);
      const availableWidth = Math.max(0, widthWithoutStrokeOffset - inset);
      if (textAlign === 'center') {
        offsets.set(lineNumber, (availableWidth - contentWidth) / 2);
        return;
      }

      if (textAlign === 'right') {
        offsets.set(lineNumber, availableWidth - contentWidth);
        return;
      }

      offsets.set(lineNumber, 0);
    });

    return offsets;
  }, [
    element.textAlign,
    hasMixedTypography,
    lineContentMetrics,
    widthWithoutStrokeOffset,
  ]);

  const getSpanAlignOffset = useCallback(
    (span: typeof spans[number]) =>
      span.isListMarker ? 0 : lineAlignOffsets.get(span.lineNumber) ?? 0,
    [lineAlignOffsets]
  );

  const { inlineGradientDefs, spanGradientIds } = useMemo(() => {
    const spanIds = new Map<number, string>();
    const defs: Array<{
      id: string;
      pattern: Pattern;
      bounds: { x: number; y: number; width: number; height: number };
    }> = [];
    let currentRun: {
      gradientKey: string;
      pattern: Pattern;
      id: string;
      xMin: number;
      xMax: number;
      yMin: number;
      yMax: number;
    } | null = null;

    const commitRun = (run: NonNullable<typeof currentRun>) => {
      defs.push({
        id: run.id,
        pattern: run.pattern,
        bounds: {
          x: run.xMin,
          y: run.yMin,
          width: run.xMax - run.xMin,
          height: run.yMax - run.yMin,
        },
      });
    };

    spans.forEach((span, spanIndex) => {
      if (!span.gradient || !span.gradientKey) {
        if (currentRun) {
          commitRun(currentRun);
          currentRun = null;
        }
        return;
      }

      if (!currentRun || currentRun.gradientKey !== span.gradientKey) {
        if (currentRun) {
          commitRun(currentRun);
        }
        currentRun = {
          gradientKey: span.gradientKey,
          pattern: span.gradient,
          id: `text-inline-gradient-${element.id}-${type}-${defs.length}`,
          xMin: Number.POSITIVE_INFINITY,
          xMax: Number.NEGATIVE_INFINITY,
          yMin: Number.POSITIVE_INFINITY,
          yMax: Number.NEGATIVE_INFINITY,
        };
      }

      const spanX = strokeOffset + getSpanAlignOffset(span) + span.x;
      const mixedLine = mixedLineGeometryByNumber.get(span.lineNumber);
      const spanY =
        strokeOffset +
        (mixedLine
          ? mixedLine.top
          : lineSpacing * span.lineNumber + span.lineOffsetY) +
        geometricListMarkerTextOffsetY;
      const spanHeight = mixedLine?.height || lineHeightInPixels;

      currentRun.xMin = Math.min(currentRun.xMin, spanX);
      currentRun.xMax = Math.max(currentRun.xMax, spanX + span.width);
      currentRun.yMin = Math.min(currentRun.yMin, spanY);
      currentRun.yMax = Math.max(currentRun.yMax, spanY + spanHeight);

      spanIds.set(spanIndex, currentRun.id);
    });

    if (currentRun) {
      commitRun(currentRun);
    }

    return { inlineGradientDefs: defs, spanGradientIds: spanIds };
  }, [
    element.id,
    getSpanAlignOffset,
    geometricListMarkerTextOffsetY,
    lineHeightInPixels,
    lineSpacing,
    mixedLineGeometryByNumber,
    spans,
    strokeOffset,
    type,
  ]);

  const getLineSpans = useCallback(
    (
      includeFill: boolean,
      includeListMarkers = true,
      onlyListMarkers = false
    ) => {
      const getFill = (span: typeof spans[number], spanIndex: number) => {
        if (!includeFill) {
          return undefined;
        }
        if (span.isListMarker) {
          return span.color;
        }
        if (hasTextTexture) {
          return undefined;
        }
        const gradientId = spanGradientIds.get(spanIndex);
        if (gradientId) {
          return `url(#${gradientId})`;
        }
        if (!hasFillGradient || span.hasInlineColor) {
          return span.color;
        }
        return undefined;
      };
      const getContent = (span: typeof spans[number]) =>
        applyTextTransform(span.content, span.textTransform);

      // We can't just set `dy` to the line height because it doesn't work with highlighted text which adds margins
      // so we need to calculate the y position manually for each line
      return spans.map((span, spanIndex) => {
        if (
          span.markerShape ||
          (!includeListMarkers && span.isListMarker) ||
          (onlyListMarkers && !span.isListMarker)
        ) {
          return null;
        }

        return (
          <tspan
            key={spanIndex}
            x={strokeOffset + getSpanAlignOffset(span) + span.x}
            fontWeight={span.fontWeight}
            fontStyle={span.fontStyle}
            fontFamily={
              hasMixedTypography
                ? generateFontFamily(span.font, span.renderFamily)
                : undefined
            }
            fontSize={`${span.fontSize}px`}
            letterSpacing={span.letterSpacing}
            y={
              strokeOffset +
              (hasMixedTypography
                ? mixedLineGeometryByNumber.get(span.lineNumber)?.baseline || 0
                : lineSpacing * span.lineNumber +
                  span.lineOffsetY +
                  (baselineOffset ?? 0)) +
              geometricListMarkerTextOffsetY +
              (span.markerOffsetY ?? 0)
            }
            textDecoration={span.textDecoration}
            fill={getFill(span, spanIndex)}
            dominantBaseline={
              hasMixedTypography
                ? 'alphabetic'
                : sharedTextStyles.dominantBaseline
            }
          >
            {getContent(span)}
          </tspan>
        );
      });
    },
    [
      hasFillGradient,
      hasTextTexture,
      baselineOffset,
      hasMixedTypography,
      getSpanAlignOffset,
      geometricListMarkerTextOffsetY,
      lineSpacing,
      mixedLineGeometryByNumber,
      sharedTextStyles.dominantBaseline,
      spanGradientIds,
      spans,
      strokeOffset,
    ]
  );

  const markerShapes = useMemo(
    () =>
      spans.map((span, spanIndex) => {
        if (
          !span.markerShape ||
          !span.markerShapeWidth ||
          !span.markerShapeHeight
        ) {
          return null;
        }

        const x = strokeOffset + span.x;
        const mixedLine = mixedLineGeometryByNumber.get(span.lineNumber);
        const y =
          strokeOffset +
          (mixedLine
            ? mixedLine.top
            : lineSpacing * span.lineNumber + span.lineOffsetY) +
          (span.markerShapeOffsetY ?? 0);

        if (span.markerShape === 'square') {
          return (
            <rect
              key={spanIndex}
              data-list-marker-shape="square"
              x={x}
              y={y}
              width={span.markerShapeWidth}
              height={span.markerShapeHeight}
              fill={span.color}
            />
          );
        }

        const strokeWidth =
          span.markerShape === 'circle' ? span.markerShapeStrokeWidth || 0 : 0;
        const radiusX = Math.max(0, (span.markerShapeWidth - strokeWidth) / 2);
        const radiusY = Math.max(0, (span.markerShapeHeight - strokeWidth) / 2);

        return (
          <ellipse
            key={spanIndex}
            data-list-marker-shape={span.markerShape}
            cx={x + span.markerShapeWidth / 2}
            cy={y + span.markerShapeHeight / 2}
            rx={radiusX}
            ry={radiusY}
            fill={span.markerShape === 'circle' ? 'none' : span.color}
            stroke={span.markerShape === 'circle' ? span.color : undefined}
            strokeWidth={strokeWidth || undefined}
          />
        );
      }),
    [lineSpacing, mixedLineGeometryByNumber, spans, strokeOffset]
  );

  const hasBorder = insideStrokeWidth > 0 || outsideStrokeWidth > 0;
  const hasInlineGradients = !hasTextTexture && inlineGradientDefs.length > 0;
  const fill = hasTextTexture
    ? `url(#${fillTextureId})`
    : hasFillGradient
    ? `url(#${fillGradientId})`
    : undefined;
  const textureTransform = getTextTextureTransform(
    textTexture,
    textureWidth,
    textureHeight
  );
  // Include italic ink and trimmed ascenders/descenders beyond the layout box.
  const overhang = Math.max(
    fontSize,
    ...spans.map((span) => span.fontSize || 0)
  );
  const paintLeft =
    Math.min(
      0,
      ...spans.map((span) => strokeOffset + getSpanAlignOffset(span) + span.x)
    ) - overhang;
  const paintRight =
    Math.max(
      width,
      ...spans.map(
        (span) => strokeOffset + getSpanAlignOffset(span) + span.x + span.width
      )
    ) + overhang;
  // Standard trim can leave the SVG viewport shorter than its rendered lines.
  const paintTop =
    Math.min(
      0,
      ...spans.map(
        (span) =>
          strokeOffset +
          (mixedLineGeometryByNumber.get(span.lineNumber)?.top ??
            lineSpacing * span.lineNumber + span.lineOffsetY) +
          geometricListMarkerTextOffsetY
      )
    ) - overhang;
  const paintBottom =
    Math.max(
      height,
      ...spans.map(
        (span) =>
          strokeOffset +
          (mixedLineGeometryByNumber.get(span.lineNumber)?.bottom ??
            lineSpacing * span.lineNumber +
              span.lineOffsetY +
              lineHeightInPixels) +
          geometricListMarkerTextOffsetY
      )
    ) + overhang;
  const texturePaint =
    textureTransform &&
    getTextTexturePaint(
      textureTransform,
      {
        x: (textureOrigin?.x ?? -left) + strokeOffset,
        y:
          (textureOrigin?.y ?? -top) +
          strokeOffset +
          geometricListMarkerTextOffsetY,
      },
      {
        x: paintLeft,
        y: paintTop,
        width: paintRight - paintLeft,
        height: paintBottom - paintTop,
      }
    );

  const texturePreserveAspectRatio = getTexturePreserveAspectRatio(
    textTexture?.sizing,
    textTexture?.position
  );

  // Photoshop casts Drop Shadow from the layer's own alpha; its Stroke effect
  // casts nothing. Paint the outside stroke after the shadow group so it sits
  // on top of the shadow without widening it. Without a shadow it stays in the
  // group, so the markup is unchanged.
  const outsideStrokeAboveShadow = hasShadow && outsideStrokeWidth > 0;
  const outsideStroke = outsideStrokeWidth > 0 && (
    <text
      style={{
        ...sharedTextStyles,
        ...sharedStrokeStyles,
        fill: 'none',
        strokeWidth: dataToEditorY(outsideStrokeWidth * 2),
      }}
      mask={`url(#${outsideTextClipPathId})`}
      {...sharedTextProps}
    >
      {getLineSpans(false)}
    </text>
  );

  // Glow paints the fill before bevel shading; strokes and drop shadow stay separate.
  const textFill = (
    <text
      fill={fill}
      filter={
        scaledGlow
          ? `url(#${glowFilterId})`
          : scaledDepth
          ? `url(#${depthFilterId})`
          : undefined
      }
      style={{
        ...sharedTextStyles,
      }}
      {...sharedTextProps}
    >
      {getLineSpans(true, !scaledGlow && !scaledDepth)}
    </text>
  );
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{
        ...styles,
        position: 'absolute',
        top: `${top - strokeOffset - geometricListMarkerTextOffsetY}px`,
        left: `${left - strokeOffset}px`,
        zIndex: 1,
        pointerEvents: 'none',
        width: `${width}px`,
        height: `${height}px`,
        overflow: 'visible',
      }}
    >
      {/* <title>Text preview</title> */}

      {/*
        Clip path for the stroke
        (since the stroke property doesn't support stroke alignment)
        */}
      {(hasBorder ||
        hasFillGradient ||
        hasTextTexture ||
        hasInlineGradients ||
        hasShadow ||
        scaledDepth ||
        scaledGlow) && (
        <defs>
          {scaledGlow && (
            <TextInnerGlowFilter
              id={glowFilterId}
              glow={scaledGlow}
              width={width}
              height={height}
            />
          )}
          {scaledDepth && (
            <TextDepthFilter
              id={depthFilterId}
              previewElementId={element.id}
              previewDepthFor={previewDepthFor}
              cachePreview={cachePreview}
              depth={scaledDepth}
              width={width}
              height={height}
            />
          )}
          {hasShadow && shadowProps && (
            <filter
              id={shadowFilterId}
              filterUnits="userSpaceOnUse"
              x={shadowProps.x}
              y={shadowProps.y}
              width={shadowProps.width}
              height={shadowProps.height}
            >
              <feDropShadow
                dx={shadowProps.dx}
                dy={shadowProps.dy}
                stdDeviation={shadowProps.blur}
                floodColor={shadowProps.color}
                floodOpacity={shadowProps.opacity}
              />
            </filter>
          )}
          {hasBorder && (
            <>
              <clipPath id={insideTextClipPathId}>
                <text
                  style={{
                    ...sharedTextStyles,
                    fill: 'black',
                  }}
                  {...sharedTextProps}
                >
                  {getLineSpans(false)}
                </text>
              </clipPath>
              <mask id={outsideTextClipPathId} maskUnits="userSpaceOnUse">
                <rect x="0" y="0" width={width} height={height} fill="white" />
                <text
                  style={{
                    ...sharedTextStyles,
                    fill: 'black',
                  }}
                  {...sharedTextProps}
                >
                  {getLineSpans(false)}
                </text>
              </mask>
              <SvgTextGradient
                gradientId={strokeGradientId}
                pattern={strokeElement.textBorder.color}
                editorBounds={{
                  x: strokeOffset,
                  y: strokeOffset,
                  width: widthWithoutStrokeOffset,
                  height: height - strokeOffset * 2,
                }}
                bounds={
                  strokeElement.textBorder.color.geometryVersion === 2
                    ? {
                        x: strokeOffset,
                        y: strokeOffset,
                        width: widthWithoutStrokeOffset,
                        height: height - strokeOffset * 2,
                      }
                    : undefined
                }
              />
            </>
          )}
          {hasFillGradient && (
            <SvgTextGradient
              gradientId={fillGradientId}
              pattern={fillPattern}
              editorBounds={
                fillGradientBounds || {
                  x: strokeOffset,
                  y: strokeOffset,
                  width: widthWithoutStrokeOffset,
                  height: height - strokeOffset * 2,
                }
              }
              bounds={
                fillGradientBounds ||
                (fillPattern.geometryVersion === 2
                  ? {
                      x: strokeOffset,
                      y: strokeOffset,
                      width: widthWithoutStrokeOffset,
                      height: height - strokeOffset * 2,
                    }
                  : undefined)
              }
            />
          )}
          {hasTextTexture && (
            <pattern
              id={fillTextureId}
              patternUnits="userSpaceOnUse"
              x={texturePaint?.pattern.x ?? strokeOffset}
              y={texturePaint?.pattern.y ?? strokeOffset}
              width={texturePaint?.pattern.width ?? textureWidth}
              height={texturePaint?.pattern.height ?? textureHeight}
              viewBox={`0 0 ${texturePaint?.pattern.width ?? textureWidth} ${
                texturePaint?.pattern.height ?? textureHeight
              }`}
            >
              <image
                href={textTexture?.src}
                x={texturePaint?.image.x ?? 0}
                y={texturePaint?.image.y ?? 0}
                width={texturePaint?.image.width ?? textureWidth}
                height={texturePaint?.image.height ?? textureHeight}
                preserveAspectRatio={
                  textureTransform ? 'none' : texturePreserveAspectRatio
                }
              />
            </pattern>
          )}
          {hasInlineGradients &&
            inlineGradientDefs.map((gradient) => (
              <SvgTextGradient
                key={gradient.id}
                gradientId={gradient.id}
                pattern={
                  preview?.inlinePattern &&
                  preview.inlinePattern.id === gradient.pattern.id
                    ? preview.inlinePattern.pattern
                    : gradient.pattern
                }
                bounds={
                  inlineGradientPreviewBounds &&
                  gradient.pattern.id === inlineGradientPreviewBounds.id
                    ? inlineGradientPreviewBounds.bounds
                    : gradient.bounds
                }
                editorBounds={gradient.bounds}
              />
            ))}
        </defs>
      )}

      <g filter={hasShadow ? `url(#${shadowFilterId})` : undefined}>
        {/*
      The text element with all the different fills and styles applied to it.
      There's a very small difference (<1px, sometimes 0px) between how SVG text is displayed
      and how regular text is displayed.
      To avoid any gaps between the text and the stroke, we display the text fill as an SVG as well.
      We also use this to support gradient text fills.
        */}
        {scaledGlow && scaledDepth ? (
          <g filter={`url(#${depthFilterId})`}>{textFill}</g>
        ) : (
          textFill
        )}
        {(scaledGlow || scaledDepth) &&
          spans.some((span) => span.isListMarker && !span.markerShape) && (
            <text
              style={sharedTextStyles}
              {...sharedTextProps}
              data-text-effect-markers=""
            >
              {getLineSpans(true, true, true)}
            </text>
          )}
        {markerShapes}

        {insideStrokeWidth > 0 && (
          <text
            style={{
              ...sharedTextStyles,
              ...sharedStrokeStyles,
              fill: 'none',
              // By default, SVG strokes are added both to the inside and the outside
              // So if we want an inside stroke that's 2px wide, we need to set the stroke width to 4px
              strokeWidth: dataToEditorY(insideStrokeWidth * 2),
            }}
            clipPath={`url(#${insideTextClipPathId})`}
            {...sharedTextProps}
          >
            {getLineSpans(false)}
          </text>
        )}
        {!outsideStrokeAboveShadow && outsideStroke}
      </g>
      {outsideStrokeAboveShadow && outsideStroke}
    </svg>
  );
});

function getGeometricListMarkerTextOffsetY({
  baselineOffset,
  font,
  fontSize,
  lineHeightInPixels,
  spans,
}: {
  baselineOffset: number | null;
  font: TextElementFont;
  fontSize: number;
  lineHeightInPixels: number;
  spans: ReturnType<typeof getSpansForElement>;
}) {
  if (
    baselineOffset !== null ||
    !spans.some(({ markerShape }) => markerShape)
  ) {
    return 0;
  }

  const { metrics } = font;
  if (
    !metrics ||
    !metrics.upm ||
    metrics.asc === undefined ||
    metrics.des === undefined
  ) {
    return Math.max(0, (lineHeightInPixels - fontSize) / 2);
  }

  const contentHeight =
    (fontSize * (metrics.asc + Math.abs(metrics.des))) / metrics.upm;

  return Math.max(0, (lineHeightInPixels - contentHeight) / 2);
}
function getTexturePreserveAspectRatio(
  sizing = 'cover',
  position = { x: 0.5, y: 0.5 }
) {
  if (sizing === 'stretch') {
    return 'none';
  }

  const x = typeof position.x === 'number' ? position.x : 0.5;
  const y = typeof position.y === 'number' ? position.y : 0.5;
  const xAlignment = x <= 0 ? 'xMin' : x >= 1 ? 'xMax' : 'xMid';
  const yAlignment = y <= 0 ? 'YMin' : y >= 1 ? 'YMax' : 'YMid';
  const meetOrSlice = sizing === 'contain' ? 'meet' : 'slice';

  return `${xAlignment}${yAlignment} ${meetOrSlice}`;
}
